import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Table, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import ReactivateNumbersModal from "views/Components/ReactivateNumbersModal";

const PACKAGES = [
  { id: "pkg_25", amount: 25, credits: "2,500", color: "#6c757d" },
  { id: "pkg_50", amount: 50, credits: "5,000", color: "#17a2b8" },
  { id: "pkg_100", amount: 100, credits: "10,000", color: "#014c95", popular: true },
  { id: "pkg_250", amount: 250, credits: "25,000", color: "#28a745" },
  { id: "pkg_500", amount: 500, credits: "50,000", color: "#fd7e14" },
  { id: "pkg_1000", amount: 1000, credits: "100,000", color: "#dc3545" }
];

function parseJwt(token) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
}

function Billing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [purchasing, setPurchasing] = useState(null);
  const [deactivatedNumbers, setDeactivatedNumbers] = useState([]);
  const [showReactivateModal, setShowReactivateModal] = useState(false);

  const fetchPhoneNumbers = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API_BASE_URL}/phone-numbers/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const inactive = res.data.phoneNumbers.filter(n => {
          const daysSinceLastBill = n.lastBilledAt 
            ? (Date.now() - new Date(n.lastBilledAt).getTime()) / (1000 * 60 * 60 * 24)
            : 30;
          return n.isPastDue || (!n.isActive && daysSinceLastBill >= 29.5);
        });
        setDeactivatedNumbers(inactive);
      }
    } catch (err) {
      console.error("Failed to fetch phone numbers on billing", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        
        const decoded = parseJwt(token);
        if (!decoded || !decoded.userId) return;

        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch user to get credits
        const userRes = await axios.get(`${API_BASE_URL}/users/${decoded.userId}`, { headers });
        if (userRes.data.success && userRes.data.data) {
          setCredits(userRes.data.data.credits || 0);
        }

        // Fetch transactions
        const txRes = await axios.get(`${API_BASE_URL}/billing/transactions`, { headers });
        if (txRes.data.success) {
          setTransactions(txRes.data.transactions);
        }

        await fetchPhoneNumbers();
      } catch (error) {
        console.error("Error fetching billing data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleBuy = async (pkg) => {
    setPurchasing(pkg.amount);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE_URL}/billing/buy-credits`,
        { 
          amount: pkg.amount,
          successUrl: `${window.location.origin}/admin/billing?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/admin/billing`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error("Purchase error:", error);
      alert(error.response?.data?.message || "Failed to initiate purchase.");
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col lg={12} className="text-center">
          <h2 className="fw-bold mb-1 mt-0" style={{ fontWeight: "700" }}>Wallet & Billing</h2>
          <p className="text-muted" style={{ fontWeight: "600", fontSize: "1.1rem" }}>
            Add SMS credits to your account and view your transaction history.
          </p>
        </Col>
      </Row>

      {deactivatedNumbers.length > 0 && (
        <Row className="mb-4">
          <Col lg={12}>
            <Alert 
              variant="danger" 
              className="border-0 shadow-sm p-4 d-flex align-items-center justify-content-between flex-column flex-md-row" 
              style={{ borderRadius: "12px", backgroundColor: "#f8d7da", borderLeft: "5px solid #dc3545" }}
            >
              <div className="text-start">
                <h4 className="alert-heading font-weight-bold mt-0 mb-1" style={{ color: "#721c24", fontWeight: "700", fontSize: "1.1rem" }}>
                  <i className="fas fa-exclamation-circle me-2"></i> Action Required: Deactivated Phone Numbers
                </h4>
                <p className="mb-0 text-dark font-weight-bold" style={{ fontSize: "0.95rem" }}>
                  You have <strong>{deactivatedNumbers.length}</strong> phone number(s) deactivated due to insufficient credits. Reactivate them to resume routing.
                </p>
              </div>
              <Button 
                onClick={() => setShowReactivateModal(true)}
                className="mt-3 mt-md-0 fw-bold btn-primary"
                style={{ fontWeight: "700", borderRadius: "8px", padding: "10px 25px" }}
              >
                Reactivate Numbers
              </Button>
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="justify-content-center mb-5">
        <Col md={6} className="text-center">
          <Card className="shadow-sm" style={{ borderRadius: "16px", border: "2px solid #ECECEC" }}>
            <Card.Body className="p-4">
              <h5 className="text-muted text-uppercase mb-2" style={{ fontWeight: "600", letterSpacing: "1px" }}>Current Balance</h5>
              <h1 className="display-3 fw-bold" style={{ color: "#014c95", fontWeight: "800" }}>
                {credits.toLocaleString()} Credits
              </h1>
              <p className="text-muted mb-0">Total available funds for SMS campaigns</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="justify-content-center mb-5">
        {PACKAGES.map((pkg) => (
          <Col md={4} key={pkg.id} className="mb-4 d-flex">
            <Card
              className="text-center w-100 flex-fill"
              style={{
                borderRadius: "16px",
                border: pkg.popular ? "2px solid #014c95" : "2px solid #ECECEC",
                boxShadow: pkg.popular ? "0 8px 16px rgba(1,76,149,0.15)" : "0 4px 8px rgba(0,0,0,0.05)",
                transform: pkg.popular ? "scale(1.03)" : "scale(1)",
                zIndex: pkg.popular ? 10 : 1,
              }}
            >
              {pkg.popular && (
                <div
                  className="bg-primary text-white py-1 fw-bold w-100"
                  style={{ borderTopLeftRadius: "14px", borderTopRightRadius: "14px", backgroundColor: "#014c95" }}
                >
                  MOST POPULAR
                </div>
              )}
              <Card.Body className="d-flex flex-column p-4">
                <h3 className="fw-bold mt-2" style={{ fontWeight: "700" }}>Buy ${pkg.amount}</h3>
                <p className="text-muted mb-4" style={{ fontSize: "1.1rem" }}>Adds {pkg.credits} credits</p>
                <Button
                  className="w-100 py-3 fw-bold fs-6 mt-auto text-white"
                  style={{
                    backgroundColor: pkg.color,
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "700"
                  }}
                  onClick={() => handleBuy(pkg)}
                  disabled={purchasing !== null}
                >
                  {purchasing === pkg.amount ? "Processing..." : `Buy $${pkg.amount}`}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row>
        <Col lg={12}>
          <h3 className="fw-bold mb-3" style={{ fontWeight: "700" }}>Transaction History</h3>
          <div
            className="p-0"
            style={{
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              backgroundColor: "#fff",
              border: "2px solid #ECECEC",
            }}
          >
            <Table responsive className="mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead className="table-light">
                <tr style={{ borderBottom: "2px solid #c7c7c7" }}>
                  <th style={{ fontWeight: "700", padding: "12px 20px" }}>Date</th>
                  <th style={{ fontWeight: "700", padding: "12px 20px" }}>Description</th>
                  <th style={{ fontWeight: "700", padding: "12px 20px" }}>Amount</th>
                  <th style={{ fontWeight: "700", padding: "12px 20px" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length > 0 ? (
                  transactions.map((tx) => (
                    <tr key={tx._id} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ fontWeight: "600", padding: "15px 20px" }}>
                        {new Date(tx.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ fontWeight: "600", padding: "15px 20px" }}>{tx.description}</td>
                      <td style={{ fontWeight: "700", padding: "15px 20px" }}>
                        ${(tx.amount / 100).toFixed(2)}
                      </td>
                      <td style={{ padding: "15px 20px" }}>
                        <span className={`badge px-2 py-1 ${tx.status === 'succeeded' ? 'bg-success' : 'bg-danger'}`}>
                          {tx.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center fw-bold p-5 text-muted">
                      No transaction history found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>
      <ReactivateNumbersModal 
        show={showReactivateModal}
        onHide={() => setShowReactivateModal(false)}
        deactivatedNumbers={deactivatedNumbers}
        userCredits={credits || 0}
        onSuccess={(updatedCredits) => {
          setCredits(updatedCredits);
          fetchPhoneNumbers();
        }}
      />

    </Container>
  );
}

export default Billing;
